// Copyright (C) 2012-present, The Authors. This program is free software: you can redistribute it and/or  modify it under the terms of the GNU Affero General Public License, version 3, as published by the Free Software Foundation. This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU Affero General Public License for more details. You should have received a copy of the GNU Affero General Public License along with this program.  If not, see <http://www.gnu.org/licenses/>.

import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { Box, Heading, Text, Button, Flex } from 'theme-ui'
import strings from '../../../strings/strings'
import PolisNet from '../../../util/net'

const DIMENSIONS = [
  { key: 'gender', label: strings('mon_dim_gender') },
  { key: 'age_band', label: strings('mon_dim_age_group') },
  { key: 'social_status', label: strings('mon_dim_social_status') },
  { key: 'employment_status', label: strings('mon_dim_work_situation') }
]

const DimensionTable = ({ label, rows }) => {
  if (!rows || rows.length === 0) return null
  return (
    <Box sx={{ mb: [3] }}>
      <Heading as="h5" sx={{ fontSize: [1], fontWeight: 600, mb: [1] }}>
        {label}
      </Heading>
      <Box as="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: [1] }}>
        <tbody>
          {rows.map((row, i) => (
            <Box
              as="tr"
              key={i}
              sx={{ borderBottom: '1px solid', borderColor: 'muted' }}
            >
              <Box as="td" sx={{ py: [1], pr: [3] }}>
                <Text sx={{ color: row.value === '(unknown)' ? 'muted' : 'text' }}>
                  {row.value}
                </Text>
              </Box>
              <Box as="td" sx={{ py: [1], textAlign: 'right' }}>
                <Text sx={{ fontWeight: 700 }}>{row.participants}</Text>
              </Box>
            </Box>
          ))}
        </tbody>
      </Box>
    </Box>
  )
}

DimensionTable.propTypes = {
  label: PropTypes.string,
  rows: PropTypes.array
}

const DemographicsStats = ({ conversation_id }) => {
  const [stats, setStats] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!conversation_id) return
    PolisNet.polisGet('/api/v3/demographics/stats', { conversation_id })
      .then((data) => setStats(data.stats || {}))
      .catch(() => setError('Could not load demographics stats'))
  }, [conversation_id])

  const handleExport = async () => {
    try {
      await PolisNet.downloadCsv(
        '/api/v3/demographics/export',
        { conversation_id },
        `demographics_${conversation_id}.csv`
      )
    } catch (e) {
      console.error('Demographics export error', e)
    }
  }

  if (error) return null
  if (!stats) return null

  const hasAny = DIMENSIONS.some((d) => (stats[d.key] || []).length > 0)
  if (!hasAny) return null

  return (
    <Box sx={{ mt: [4] }}>
      <Flex sx={{ alignItems: 'center', justifyContent: 'space-between', mb: [2] }}>
        <Heading as="h4" sx={{ fontSize: [2], fontWeight: 600 }}>
          {strings('mon_demographics')}
        </Heading>
        <Button
          variant="outline"
          sx={{ fontSize: [1], px: [2], py: [1], cursor: 'pointer' }}
          onClick={handleExport}
        >
          ↓ {strings('mon_download_csv')}
        </Button>
      </Flex>

      {DIMENSIONS.map((d) => (
        <DimensionTable key={d.key} label={d.label} rows={stats[d.key]} />
      ))}
    </Box>
  )
}

DemographicsStats.propTypes = {
  conversation_id: PropTypes.string
}

export default DemographicsStats
